import {
  ArrowsCounterClockwiseIcon,
  ClipboardTextIcon,
  CoinIcon,
  EnvelopeSimpleIcon,
  GavelIcon,
  PackageIcon,
  PaintBrushBroadIcon,
  PlugsIcon,
  ReceiptIcon,
  SparkleIcon,
  StorefrontIcon,
  TruckIcon,
  UserCircleIcon,
} from '@phosphor-icons/react'

import { ReturnProtectionIcon, ReturnsPortalIcon } from '@reveni-io/reveni-ui'

import { subscriptionPlans, userPermissions } from '@/common/constants'
import { settingsTypes } from '@/configuration/constants'
import { providers } from '@/returns/constants'
import routes from '@/Router/routes.constants'

import { configurationTypes, exchangeMethods, tabIds, tabList } from './constants'

export const filterHiddenTabs = tabs =>
  tabs
    .filter(tab => !tab.hidden)
    .map(tab => (tab.children ? { ...tab, children: tab.children.filter(child => !child.hidden) } : tab))

export const getTabs = ({
  t,
  subscriptionPlan,
  hasPermission,
  isOwner,
  allowsShipments,
  isUnifiedDashboard,
  allowsEmailEditor,
  virtualTryonEnabled,
  recommendationWidgetEnabled,
  bracketingWidgetEnabled,
  hasReturnProtection,
}) => {
  let merchantTabs = [
    {
      id: tabIds.returnPolicy,
      label: t(tabList.returnPolicy),
      Icon: GavelIcon,
      path: routes.merchantConfigurationPage.returnPolicy,
    },
    {
      id: tabIds.refunds,
      label: t(tabList.refunds),
      Icon: CoinIcon,
      path: routes.merchantConfigurationPage.refunds,
      disabled: !hasPermission(userPermissions.viewRefunds),
    },
    {
      id: tabIds.branding,
      label: t(tabList.branding),
      Icon: PaintBrushBroadIcon,
      path: routes.merchantConfigurationPage.branding,
    },
    {
      id: tabIds.communications,
      label: t(tabList.communications),
      Icon: EnvelopeSimpleIcon,
      children: [
        {
          id: `${tabIds.communications}-general`,
          path: routes.merchantConfigurationPage.communications,
          label: t(tabList.communicationsGeneral),
          disabled: !hasPermission(userPermissions.viewCommunications),
        },
        {
          id: `${tabIds.communications}-email-templates`,
          path: routes.merchantConfigurationPage.emailTemplates,
          label: t(tabList.emailTemplates),
          disabled: !hasPermission(userPermissions.viewCommunications) || !allowsEmailEditor,
          pages: [
            {
              label: t(tabList.emailTemplateDetail),
              path: routes.merchantConfigurationPage.emailTemplateDetail,
            },
          ],
        },
        {
          id: `${tabIds.communications}-themes`,
          path: routes.merchantConfigurationPage.themes,
          label: t(tabList.themes),
          disabled: !hasPermission(userPermissions.viewCommunications) || !allowsEmailEditor,
        },
      ],
    },
  ]

  const isBasicPlan = subscriptionPlan === subscriptionPlans.basic
  const noUserAccess = !isOwner() && !hasPermission(userPermissions.viewUsers)

  const tabs = [
    {
      id: tabIds.store,
      label: t(tabList.store),
      Icon: StorefrontIcon,
      path: routes.configurationPage.store,
      disabled: !hasPermission(userPermissions.viewStore),
    },
    {
      id: tabIds.policy,
      label: t(tabList.returnPolicy),
      Icon: GavelIcon,
      disabled:
        hasPermission(userPermissions.viewAllowedList) ||
        hasPermission(userPermissions.viewPolicy) ||
        hasPermission(userPermissions.viewPolicyRules)
          ? false
          : true,
      children: [
        {
          id: `${tabIds.policy}-config`,
          label: t('Política predeterminada'),
          path: routes.configurationPage.returnPolicy.config,
          disabled: !hasPermission(userPermissions.viewPolicy),
        },
        {
          id: `${tabIds.policy}-policy-rules`,
          label: t('Reglas condicionales'),
          path: routes.configurationPage.returnPolicy.policyRules,
          disabled: !hasPermission(userPermissions.viewPolicyRules),
          pages: [
            {
              label: t(tabList.policyRuleDetail),
              path: routes.configurationPage.returnPolicy.policyRuleDetail,
            },
          ],
        },
        {
          id: `${tabIds.policy}-exceptions`,
          label: t('Excepciones'),
          path: routes.configurationPage.returnPolicy.exceptions,
          disabled: !hasPermission(userPermissions.viewAllowedList),
          pages: [
            {
              label: t(tabList.newException),
              path: routes.configurationPage.returnPolicy.exceptionDetail,
            },
          ],
        },
      ],
    },
    {
      id: tabIds.refunds,
      label: t(tabList.refunds),
      Icon: CoinIcon,
      path: routes.configurationPage.refunds,
      disabled: !hasPermission(userPermissions.viewRefunds),
    },
    {
      id: tabIds.logistics,
      label: t(tabList.logistics),
      Icon: TruckIcon,
      children: [
        {
          id: `${tabIds.logistics}-general`,
          label: t(tabList.general),
          path: routes.configurationPage.logistics.general,
          disabled: !hasPermission(userPermissions.viewLogistics),
          hidden: !allowsShipments,
        },
        {
          id: `${tabIds.logistics}-shipping-methods`,
          label: t('Métodos envío'),
          path: routes.configurationPage.logistics.shippingMethods,
          disabled: !hasPermission(userPermissions.viewLogistics),
          hidden: !allowsShipments,
          pages: [
            {
              label: t(tabList.shippingMethodDetail),
              path: routes.configurationPage.logistics.shippingMethodDetailConfiguration,
            },
            {
              label: t(tabList.availabilityCriterionDetail),
              path: routes.configurationPage.logistics.shippingAvailabilityCriterionDetail,
            },
          ],
        },
        {
          id: `${tabIds.logistics}-delivery-methods`,
          label: t('Métodos devolución'),
          path: routes.configurationPage.logistics.deliveryMethods,
          disabled: !hasPermission(userPermissions.viewLogistics),
          pages: [
            {
              label: t(tabList.logisticsDetail),
              path: routes.configurationPage.logistics.deliveryMethodDetailConfiguration,
            },
            {
              label: t(tabList.availabilityCriterionDetail),
              path: routes.configurationPage.logistics.availabilityCriterionDetail,
            },
          ],
        },
        {
          id: `${tabIds.logistics}-return-instructions`,
          label: t('Instrucciones devolución'),
          path: routes.configurationPage.logistics.returnInstructions,
          disabled: !hasPermission(userPermissions.viewLogistics),
          pages: [
            {
              label: t(tabList.returnInstructionsDetail),
              path: routes.configurationPage.logistics.returnInstructionDetailConfiguration,
            },
          ],
        },
        {
          id: `${tabIds.logistics}-locations`,
          label: t('Ubicaciones'),
          path: routes.configurationPage.logistics.locations,
          disabled: !hasPermission(userPermissions.viewLogistics),
          pages: [
            {
              label: t(tabList.locationDetail),
              path: routes.configurationPage.logistics.locationDetailConfiguration,
            },
          ],
        },
        {
          id: `${tabIds.logistics}-packaging`,
          label: t('Embalaje'),
          path: routes.configurationPage.logistics.packaging,
          disabled: !hasPermission(userPermissions.viewLogistics),
          hidden: !allowsShipments,
          pages: [
            {
              label: t(tabList.packagingDetail),
              path: routes.configurationPage.logistics.packagingDetailConfiguration,
            },
          ],
        },
      ],
    },
    {
      id: tabIds.returnProtection,
      label: t(tabList.returnProtection),
      Icon: ReturnProtectionIcon,
      hidden: !hasReturnProtection || !hasPermission(userPermissions.viewReturnProtection),
      children: [
        {
          id: `${tabIds.returnProtection}-general`,
          label: t(tabList.returnProtectionChildren.general),
          path: routes.configurationPage.returnProtection.general,
        },
        {
          id: `${tabIds.returnProtection}-appearance`,
          label: t(tabList.returnProtectionChildren.appearance),
          path: routes.configurationPage.returnProtection.appearance,
        },
      ],
    },
    {
      id: tabIds.reasons,
      label: t(tabList.reasons),
      Icon: ArrowsCounterClockwiseIcon,
      disabled: !hasPermission(userPermissions.viewReturnReasons),
      hidden: isBasicPlan,
      children: [
        {
          id: `${tabIds.reasons}-list`,
          label: t(tabList.reasonsChildren.list),
          path: routes.configurationPage.returnReasons,
          disabled: !hasPermission(userPermissions.viewReturnReasons),
          pages: [{ label: t(tabList.reasonsDetail), path: routes.configurationPage.returnReasonsDetail }],
        },
        {
          id: `${tabIds.reasons}-settings`,
          label: t(tabList.reasonsChildren.settings),
          path: routes.configurationPage.returnReasonsSettings,
          disabled: !hasPermission(userPermissions.viewReturnReasons),
        },
      ],
    },
    {
      id: tabIds.inventory,
      label: t(tabList.inventory),
      Icon: ClipboardTextIcon,
      path: routes.configurationPage.inventory,
      disabled: !hasPermission(userPermissions.viewShipmentsSettings),
      hidden: !allowsShipments,
    },
    {
      id: tabIds.branding,
      label: t(tabList.branding),
      Icon: PaintBrushBroadIcon,
      path: routes.configurationPage.branding,
      disabled: !hasPermission(userPermissions.viewBranding),
    },
    {
      id: tabIds.orderTrackingPage,
      label: t(tabList.orderTrackingPage),
      Icon: PackageIcon,
      path: routes.configurationPage.orderTrackingPage.config,
      hidden: !hasPermission(userPermissions.viewOrderTrackingPage),
      pages: [
        {
          label: t(tabList.visibilityCriterionDetail),
          path: routes.configurationPage.orderTrackingPage.visibilityCriterionDetail,
        },
      ],
    },
    {
      id: tabIds.returnsPortal,
      label: t(tabList.returnsPortal),
      Icon: ReturnsPortalIcon,
      children: [
        {
          id: `${tabIds.returnsPortal}-configuration`,
          path: routes.configurationPage.returnsPortal.config,
          label: t(tabList.returnsPortalChildren.configuration),
        },
        {
          id: `${tabIds.returnsPortal}-copies`,
          path: routes.configurationPage.returnsPortal.copies,
          label: t(tabList.returnsPortalChildren.copies),
        },
      ],
    },
    {
      id: tabIds.communications,
      label: t(tabList.communications),
      Icon: EnvelopeSimpleIcon,
      children: [
        {
          id: `${tabIds.communications}-general`,
          path: routes.configurationPage.communications,
          label: t(tabList.communicationsGeneral),
          disabled: !hasPermission(userPermissions.viewCommunications),
        },
        {
          id: `${tabIds.communications}-email-templates`,
          path: routes.configurationPage.emailTemplates,
          label: t(tabList.emailTemplates),
          disabled: !hasPermission(userPermissions.viewCommunications) || !allowsEmailEditor,
          pages: [
            {
              label: t(tabList.emailTemplateDetail),
              path: routes.configurationPage.emailTemplateDetail,
            },
          ],
        },
        {
          id: `${tabIds.communications}-themes`,
          path: routes.configurationPage.themes,
          label: t(tabList.themes),
          disabled: !hasPermission(userPermissions.viewCommunications) || !allowsEmailEditor,
        },
      ],
    },
    {
      id: tabIds.billing,
      label: t(tabList.billing),
      Icon: ReceiptIcon,
      path: routes.configurationPage.billing,
      disabled: !hasPermission(userPermissions.viewBillingSettings),
      hidden: isBasicPlan,
    },
    {
      id: tabIds.integrations,
      label: t(tabList.integrations),
      Icon: PlugsIcon,
      path: routes.configurationPage.integrations,
      pages: [{ label: t(tabList.integrationDetail), path: routes.configurationPage.integrationsDetail }],
      disabled: !hasPermission(userPermissions.viewIntegrations),
      hidden: isBasicPlan,
    },
    {
      id: tabIds.users,
      label: t(tabList.users),
      Icon: UserCircleIcon,
      path: routes.configurationPage.users,
      pages: [
        { label: t(tabList.userDetail), path: routes.configurationPage.userDetail },
        {
          label: t(tabList.group),
          path: routes.configurationPage.groupDetail,
        },
      ],
      disabled: noUserAccess,
      hidden: noUserAccess,
    },
    {
      id: tabIds.extras,
      label: t(tabList.extras),
      Icon: SparkleIcon,
      disabled: !hasPermission(userPermissions.viewExtra),
      children: [
        {
          id: `${tabIds.extras}-reveni-widget`,
          path: routes.configurationPage.extras.reveniWidget,
          label: t(tabList.extrasChildren.reveniWidget),
        },
        {
          id: `${tabIds.extras}-shipping-widget`,
          path: routes.configurationPage.extras.shippingWidget,
          label: t(tabList.extrasChildren.shippingWidget),
          hidden: !allowsShipments,
        },
        {
          id: `${tabIds.extras}-sizing-widget`,
          path: routes.configurationPage.extras.sizingWidget,
          label: t(tabList.extrasChildren.sizingWidget),
          hidden: !recommendationWidgetEnabled,
        },
        {
          id: `${tabIds.extras}-bracketing-widget`,
          path: routes.configurationPage.extras.bracketingWidget,
          label: t(tabList.extrasChildren.bracketingWidget),
          hidden: !bracketingWidgetEnabled,
        },
        {
          id: `${tabIds.extras}-top-banner-widget`,
          path: routes.configurationPage.extras.topBannerWidget,
          label: t(tabList.extrasChildren.topBannerWidget),
        },
        {
          id: `${tabIds.extras}-try-on-widget`,
          path: routes.configurationPage.extras.tryOnWidget,
          label: t(tabList.extrasChildren.tryOnWidget),
          hidden: !virtualTryonEnabled,
        },
      ],
    },
  ]

  return isUnifiedDashboard ? merchantTabs : filterHiddenTabs(tabs)
}

export const focusOnError = (errors, setError) => {
  if (!errors) return null

  const firstError = Object?.keys(errors)[0]
  const element = document.getElementById(firstError)

  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    if (setError) setError(firstError)
  }
}

export const getTranslations = (supportedLanguages, terms) => {
  const translations = {}

  supportedLanguages?.forEach(lang => {
    translations[lang] = terms[lang] ?? null
  })

  return translations
}

export const getEmptyTranslations = supportedLanguages => {
  const translations = {}

  supportedLanguages.forEach(lang => {
    translations[lang] = null
  })

  return translations
}

export const getCarrierServices = (selectedCarrierService, carrierServices = []) => {
  if (selectedCarrierService?.id && !carrierServices?.find(service => service.id === selectedCarrierService.id)) {
    return [selectedCarrierService, ...carrierServices]
  }

  return carrierServices
}

export const getConfigurationMode = ({ isUnifiedDashboard, settingsType }) => {
  if (isUnifiedDashboard) {
    return configurationTypes.merchant
  } else if (settingsType) {
    return settingsType === settingsTypes.global ? configurationTypes.merchant : configurationTypes.returnsPortal
  }

  return null
}

export const shouldHaveOtherProductVariants = (provider, allowedExchangeMethods, externalConnectedGateway) => {
  return (
    (provider === providers.shopify || !!externalConnectedGateway) &&
    !allowedExchangeMethods?.includes(exchangeMethods.otherProducts)
  )
}

export const getEntityIdByConfigurationMode = ({ configurationMode, merchantId, returnPortalId }) => {
  return configurationMode === configurationTypes.merchant ? { merchantId } : { returnPortalId }
}

export const getInfoWithUpdatedPreferences = (info, preferencesPayload) => ({
  ...info,
  selectedStore: {
    ...info.selectedStore,
    preferences: { ...info.selectedStore.preferences, ...preferencesPayload },
  },
  stores: info.stores.map(store =>
    store.id === info.selectedStore.id
      ? { ...store, preferences: { ...store.preferences, ...preferencesPayload } }
      : store
  ),
})
